import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "@headlessui/react";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import { DownloadIcon, XIcon } from "../../components/icons";
import Toast from "../../common/Toast/Toast";
import renderPdf from "../../common/RenderPdf/RenderPdf";
import EntityConstants from "../../configs/EntityConstants";
import { GET } from "../../service/CRUDService";
import { useTranslation } from "react-i18next";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import HtmlArtifactFrame from "../../components/CertificateArtwork/HtmlArtifactFrame";
import { isHtmlUrl } from "../../configs/templateTypes";

const CertificatePreviewModal = ({
  isOpen,
  onClose,
  previewUrl,
  printPreviewUrl,
  isLoading,
  credential_id,
}) => {
  const { t } = useTranslation();
  const pdfCanvasRef = useRef(null);
  const imgRef = useRef(null);
  const digitalHtmlUrl = isHtmlUrl(previewUrl) ? previewUrl : "";
  const hasDigitalPreview = Boolean(previewUrl);

  const [currentView, setCurrentView] = useState("print");
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

  useEffect(() => {
    if (isOpen) {
      setCurrentView(printPreviewUrl ? "print" : "digital");
      setPdfLoaded(false);
      setImageLoaded(false);
    }
  }, [isOpen, printPreviewUrl]);

  useEffect(() => {
    if (isOpen && currentView === "print" && printPreviewUrl && !pdfLoaded) {
      const timer = setTimeout(() => {
        if (pdfCanvasRef.current) {
          renderPdf(printPreviewUrl, pdfCanvasRef)
            .then(() => {
              setPdfLoaded(true);
            })
            .catch((error) => {
              console.error("PDF rendering failed:", error);
            });
        } else {
          console.log("Canvas not found");
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [currentView, isOpen, printPreviewUrl, pdfLoaded]);

  useEffect(() => {
    if (currentView === "digital" && hasDigitalPreview) {
      setImageLoaded(false);
    }
  }, [currentView, hasDigitalPreview, digitalHtmlUrl, previewUrl]);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageLoaded(true);
  };

  const handleDownload = async () => {
    try {
      const response = await GET(
        `${EntityConstants.DOWNLOAD_SVG_AS_PNG}${credential_id}`,
      );

      if (!response.ok) throw new Error("Failed to fetch download URL");

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const filename = contentDisposition
        ? contentDisposition.match(/filename="(.+?)"/)?.[1]
        : `${credential_id}`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to download file",
      });
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog
      open
      onClose={onClose}
      className="relative z-50"
      id={pageRootTestIds.notifyCertificatePreviewModal}
    >
      <div
        className="fixed inset-0 bg-black bg-opacity-50"
        aria-hidden="true"
      />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
          <div className="flex justify-between items-center p-4 border-b">
            <Dialog.Title as="h2" className="text-lg font-semibold">
              {t("workspace:certificate_preview")}
            </Dialog.Title>
            <div className="flex items-center gap-2">
              <button
                id="notify-certificate-preview-download"
                onClick={handleDownload}
                disabled={
                  isLoading ||
                  !(currentView === "digital"
                    ? hasDigitalPreview
                    : printPreviewUrl)
                }
                className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium ${
                  isLoading ||
                  !(currentView === "digital"
                    ? hasDigitalPreview
                    : printPreviewUrl)
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-cfPrimaryButton hover:text-cfPrimaryDark"
                } cf-focus-ring rounded-sm`}
                title={t("workspace:download_certificate")}
              >
                <DownloadIcon size={16} className="inline-flex" aria-hidden />
                <span>{t("workspace:download")}</span>
              </button>
              <button
                id="notify-certificate-preview-close"
                type="button"
                onClick={onClose}
                aria-label={t("workspace:close")}
                className="inline-flex items-center justify-center rounded-md border-0 bg-transparent p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-1"
              >
                <XIcon size={20} aria-hidden />
              </button>
            </div>
          </div>

          {hasDigitalPreview && printPreviewUrl && (
            <div className="border-b border-gray-200 mb-4 flex justify-center">
              <nav className="flex -mb-px">
                <button
                  type="button"
                  id="notify-certificate-preview-tab-digital"
                  onClick={() => setCurrentView("digital")}
                  className={`py-2 px-4 text-sm font-medium cf-focus-ring ${
                    currentView === "digital"
                      ? "border-b-2 border-cfPrimaryButton text-cfPrimaryButton"
                      : "text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent"
                  } ${
                    !hasDigitalPreview ? "cursor-not-allowed opacity-50" : ""
                  } transition-colors duration-200`}
                  disabled={!hasDigitalPreview}
                >
                  {t("workspace:digital_view")}
                </button>
                <button
                  type="button"
                  id="notify-certificate-preview-tab-print"
                  onClick={() => {
                    if (printPreviewUrl) {
                      setCurrentView("print");
                      setPdfLoaded(false);
                    }
                  }}
                  className={`py-2 px-4 text-sm font-medium cf-focus-ring ${
                    currentView === "print"
                      ? "border-b-2 border-cfPrimaryButton text-cfPrimaryButton"
                      : "text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent"
                  } ${
                    !printPreviewUrl ? "cursor-not-allowed opacity-50" : ""
                  } transition-colors duration-200`}
                  disabled={!printPreviewUrl}
                >
                  {t("workspace:print_view")}
                </button>
              </nav>
            </div>
          )}

          <div className="overflow-auto flex-1 p-4 flex items-center justify-center relative">
            {isLoading ? (
              <div className="text-gray-500 text-center">
                <svg
                  className="animate-spin h-6 w-6 mx-auto mb-2 text-blue-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  ></path>
                </svg>
                {t("workspace:loading_preview")}
              </div>
            ) : currentView === "digital" ? (
              digitalHtmlUrl ? (
                <div className="h-full w-full flex items-center justify-center relative">
                  <HtmlArtifactFrame
                    url={digitalHtmlUrl}
                    title="Digital Certificate Preview"
                    maxHeightVh={60}
                    className={`${
                      imageLoaded ? "opacity-100" : "opacity-0"
                    } transition-opacity duration-200`}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                  />
                  {!imageLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                      <svg
                        className="animate-spin h-6 w-6 text-blue-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        ></path>
                      </svg>
                    </div>
                  )}
                </div>
              ) : previewUrl ? (
                <div className="h-full w-full flex items-center justify-center relative">
                  <img
                    ref={imgRef}
                    src={previewUrl}
                    className={`max-w-full max-h-[60vh] mx-auto object-contain ${
                      imageLoaded ? "opacity-100" : "opacity-0"
                    } transition-opacity duration-200`}
                    title="Certificate Preview"
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    alt="Digital Certificate Preview"
                  />
                  {!imageLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                      <div className="text-gray-500 text-center">
                        <svg
                          className="animate-spin h-6 w-6 mx-auto mb-2 text-blue-600"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          ></path>
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-full flex items-center justify-center bg-gray-50 rounded-md">
                  <p className="text-gray-500">
                    {t("workspace:digital_preview_not_available")}
                  </p>
                </div>
              )
            ) : printPreviewUrl ? (
              <div className="h-full w-full flex items-center justify-center relative">
                <canvas
                  ref={pdfCanvasRef}
                  className={`max-w-full max-h-[60vh] ${
                    pdfLoaded ? "opacity-100" : "opacity-0"
                  } transition-opacity duration-200`}
                />
                {!pdfLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                    <div className="text-gray-500 text-center">
                      <svg
                        className="animate-spin h-6 w-6 mx-auto mb-2 text-blue-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        ></path>
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center bg-gray-50 rounded-md">
                <p className="text-gray-500">
                  {t("workspace:print_preview_not_available")}
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end p-4 border-t bg-gray-50">
            <button
              id="notify-certificate-preview-footer-close"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
            >
              {t("workspace:close")}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default CertificatePreviewModal;
